// PATH: src/admin/components/CloudinaryImageField.jsx
// VERSION: 2.0 — Sin macros, Chakra-only. Sube 1 imagen y emite publicId

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Image, Input, Text, useToast, useColorModeValue } from '@chakra-ui/react';
import { FiUploadCloud, FiTrash2 } from 'react-icons/fi';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import { cld } from '../../utils/cloudinary';

export default function CloudinaryImageField({
  label = 'Imagen (Cloudinary)',
  value,                 // puede ser publicId o URL
  onChange,              // (publicIdOrUrl) => void
  folder = 'misc',       // p.ej. "events" | "albums"
  previewW = 360,
  previewH = 216,
}) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState('');

  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const emptyBorderColor = useColorModeValue('gray.300', 'gray.600');
  const emptyColor = useColorModeValue('gray.500', 'gray.400');

  // Mantener vista cuando cambia value
  useEffect(() => {
    if (!value) { setPreview(''); return; }
    // Si ya es URL, úsala; si es publicId, construimos URL
    if (/^https?:\/\//i.test(value)) {
      setPreview(value);
    } else {
      setPreview(cld(value, { w: previewW, h: previewH, crop: 'fill', gravity: 'auto' }));
    }
  }, [value, previewW, previewH]);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const res = await uploadToCloudinary(file, { folder });
      setPreview(res.secureUrl);
      onChange?.(res.publicId); // Guardamos el publicId
      toast({ status: 'success', title: 'Imagen subida' });
    } catch (err) {
      console.error(err);
      toast({ status: 'error', title: err.message || 'No se pudo subir' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <Box>
      <Text mb={1} fontWeight="semibold" color={labelColor}>{label}</Text>

      <Flex gap={2} align="center" wrap="wrap" mb={2}>
        <Button
          leftIcon={<FiUploadCloud />}
          onClick={() => inputRef.current?.click()}
          isLoading={uploading}
          loadingText="Subiendo..."
          size="sm"
        >
          Subir archivo
        </Button>
        {value && (
          <Button
            leftIcon={<FiTrash2 />}
            variant="outline"
            size="sm"
            onClick={() => onChange?.('')}
          >
            Quitar
          </Button>
        )}
        <Input
          placeholder="o pega una URL directa"
          size="sm"
          value={/^https?:\/\//i.test(value || '') ? value : ''}
          onChange={(e) => onChange?.(e.target.value)}
          w="full"
          maxW={{ base: '100%', md: '360px' }}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Flex>

      {preview ? (
        <Image
          src={preview}
          alt="preview"
          w="100%"
          maxW={`${previewW}px`}
          h="auto"
          maxH={`${previewH}px`}
          objectFit="contain"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        />
      ) : (
        <Box
          w="100%"
          maxW={`${previewW}px`}
          h={`${previewH}px`}
          border="1px dashed"
          borderColor={emptyBorderColor}
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color={emptyColor}
          fontSize="sm"
        >
          Sin imagen
        </Box>
      )}
    </Box>
  );
}
